import { useAuthStore } from '@/stores/authStore'
import { useWebSocket } from '@/composable/useWebsockets'
import { nextTick, onUnmounted, watch, type Ref } from 'vue'
import type {
  SwapAlertsFilter,
  SwapAlertSortingKey,
  SwapAlertsResponse,
} from '@/services/alerts-v2-service/types'
import type { SortOrder } from '@/services/alerts-v2-service/types'

export const useAlertsWs = (
  filters: Ref<SwapAlertsFilter>,
  sortBy: Ref<SwapAlertSortingKey>,
  sortOrder: Ref<SortOrder>,
  page: Ref<number>,
  onData: (data: SwapAlertsResponse) => void,
) => {
  const authStore = useAuthStore()

  const buildSubMessage = () => {
    const filterPayload: Record<string, unknown> = {
      ...(filters.value ?? {}),
      page: page.value,
      sort_field: sortBy.value,
      sort_order: sortOrder.value,
    }
    // Убираем null/undefined для чистоты
    Object.keys(filterPayload).forEach((key) => {
      if (filterPayload[key] === null || filterPayload[key] === undefined) {
        delete filterPayload[key]
      }
    })
    return JSON.stringify({
      action: 'watch',
      params: ['alerts', filterPayload],
    })
  }

  const onMessage = (message: string) => {
    try {
      const response = JSON.parse(message)
      console.log('[WS alerts] raw msg:', response)
      if (response.event === 'watch' && response.params?.[0] === 'alerts') {
        console.log('[WS alerts] calling onData, alerts count:', response.data?.alerts?.length)
        onData(response.data as SwapAlertsResponse)
      } else {
        console.log(
          '[WS alerts] skipped, event:',
          response.event,
          'params[0]:',
          response.params?.[0],
        )
      }
    } catch (e) {
      console.error('alerts ws parse error:', e)
    }
  }

  const { run, stop, send, status } = useWebSocket(
    import.meta.env.VITE_WS_URL + '/api/v2/ws?token=' + authStore.token,
    {
      onMessage,
      handleClose: (reconnect) => {
        window.setTimeout(() => reconnect(), 1500)
      },
    },
  )

  let inited = false

  const start = () => {
    const msg = buildSubMessage()
    run(msg)
    nextTick(() => {
      inited = true
    })
  }

  // При изменении фильтров/сортировки/страницы — переотправляем подписку
  watch(
    [filters, sortBy, sortOrder, page],
    () => {
      if (!inited) return
      const msg = buildSubMessage()
      if (status.value === 'OPEN') {
        send(msg)
      } else {
        run(msg)
      }
    },
    { deep: true },
  )

  onUnmounted(() => {
    stop()
  })

  return { start, stop, status }
}
